/**
 * monthlyActualCost.js
 * ─────────────────────────────────────────────────────────────
 * Single source of truth for Monthly Actual Cost / Movement calculations.
 *
 * This is a distinct domain from buildCostControlData.js (the cumulative
 * Cost Control engine): that engine answers "what is the total Actual Cost
 * to date for this Cost Code", period-agnostic except for a simple
 * selectedMonth/selectedYear filter. This module answers a different
 * question: "how did each Cost Code's actual cost move between the two
 * most recent periods that actually have data" — Movement is NOT a
 * calendar-month comparison, it's a comparison between whichever two
 * periods are the last two present in the underlying transactions.
 *
 * Extracted verbatim from MonthlyActualCost.jsx's loadData() (the five
 * source queries: IPC, Payroll, Store, Petty Cash, Accrual) plus the
 * grandTotals / cumulativeTotals / grandMovement / per-Cost-Code movement
 * math that used to be computed inline during render. No business rule
 * changed — same sources, same period-bucketing (dateStr.slice(0,7)),
 * same "last two periods present" definition of Movement.
 *
 * PUBLIC OUTPUT CONTRACT — three granularities of the same Movement
 * figure, all built here and all equally part of this module's public
 * surface (none is a PDF-specific or otherwise consumer-specific
 * add-on):
 *   - byCostCode      — keyed by top-level (level 1) Cost Code, via
 *                       resolveToCostCode() (climbs to the root).
 *   - bySubCode       — keyed by the raw Sub Code exactly as recorded on
 *                       each transaction (no roll-up at all).
 *   - byResourceCode  — keyed by the Sub Code's immediate parent (one
 *                       level up only), via resolveToParentCode() — the
 *                       Resource Code sitting between a Sub Code and its
 *                       top-level Cost Code in Cost Control's own
 *                       Cost Code → Resource → Sub Code hierarchy.
 * Any current or future consumer (screens, reports, Excel/PDF exports,
 * APIs) needing Movement at Cost Code, Resource, or Sub Code granularity
 * should read the matching index here rather than re-deriving it —
 * exactly as ExecutiveReport.jsx already does for byCostCode/
 * grandMovement, and Cost Control PDF now does for byResourceCode.
 *
 * Consumers:
 *   - MonthlyActualCost.jsx (the full breakdown by source/Cost Code/Sub Code)
 *   - ExecutiveReport.jsx (only needs byCostCode[code].movement — the
 *     "This Period" column in the Page 3 Cost Summary table)
 *   - Cost Control PDF (buildCostControlPdfModel.js) — reads bySubCode,
 *     byResourceCode, and byCostCode at their respective tree levels.
 *
 * IMPORTANT: byCostCode/bySubCode/byResourceCode are all keyed by the same
 * text code labels used throughout this module (e.g. "03 10 00",
 * "MAT-BEM-01"), not cost_code_id UUIDs — this matches how the underlying
 * rows are grouped (costCode/subCode are labels derived from sub_code /
 * cost_codes.code lookups, not always a resolved cost_codes.id). Callers
 * matching against buildCumulativeData()'s id-keyed rows must join by
 * code, not by id.
 * ─────────────────────────────────────────────────────────────
 */
import { supabase } from './supabase'
import { buildHierarchyFromRows, buildHierarchyLookups, resolveResourceType, resolveResourceDictCode, resolveIndirectCategory, isIndirectCostCode } from './monthlyActualCostHierarchy'

const periodToNum = (p) => p ? parseInt(p.replace('-', '')) : 0

/**
 * buildSummary — PRESENTATION MODEL ONLY, independent of buildHierarchyFromRows.
 * ─────────────────────────────────────────────────────────────
 * Repackages totals that are already fully computed by
 * buildMonthlyActualCostData (grandTotals/grandTotal/grandMovement/
 * lastPeriod/prevPeriod) into a small shape convenient for a sticky
 * "executive summary" UI section. Performs no aggregation of its own, no
 * business/financial calculation — every field is either a direct
 * reference to an already-computed value or a plain lookup
 * (grandTotals[period]) with no arithmetic beyond that lookup's `|| 0`
 * guard. Deliberately has zero knowledge of `rows` or the hierarchy tree,
 * so either this function or buildHierarchyFromRows can be removed
 * without affecting the other or the calculation pipeline.
 *
 * @param {Record<string, number>} grandTotals
 * @param {number} grandTotal
 * @param {number} grandMovement
 * @param {string|null} lastPeriod
 * @param {string|null} prevPeriod
 * @param {string[]} periods
 * @returns {{
 *   grandTotal: number, grandMovement: number,
 *   lastPeriod: string|null, prevPeriod: string|null,
 *   lastPeriodTotal: number, prevPeriodTotal: number,
 *   periodsCount: number,
 * }}
 */
function buildSummary(grandTotals, grandTotal, grandMovement, lastPeriod, prevPeriod, periods) {
  return {
    grandTotal,
    grandMovement,
    lastPeriod,
    prevPeriod,
    lastPeriodTotal: grandTotals[lastPeriod] || 0,
    prevPeriodTotal: grandTotals[prevPeriod] || 0,
    periodsCount: periods.length,
  }
}

/**
 * @param {string} projectId
 * @returns {Promise<{
 *   periods: string[],                          // e.g. ['2026-05','2026-06','2026-07'], chronological
 *   rows: Array<{ costCode, subCode, costCodeDesc, resolvedCostCodeDesc, source, periods: Record<string, number> }>,
 *                                                  // costCodeDesc: description of whichever code the
 *                                                  // transaction was posted against (Sub Code, in
 *                                                  // most cases) — unchanged, original semantics.
 *                                                  // resolvedCostCodeDesc: ADDITIVE — description of
 *                                                  // this row's resolved top-level (Level-1) Cost
 *                                                  // Code, via the same realCodeFor() resolution used
 *                                                  // by byCostCode/hierarchy. Use this for a parent
 *                                                  // Cost Code node's own description; keep using
 *                                                  // costCodeDesc for the leaf Sub Code row.
 *   grandTotals: Record<string, number>,         // period -> total across all sources/codes
 *   grandTotal: number,                          // sum of grandTotals across all periods
 *   cumulativeTotals: Record<string, number>,    // running total up to and including each period
 *   grandMovement: number,                       // grandTotals[lastPeriod] - grandTotals[prevPeriod]
 *   lastPeriod: string | null,
 *   prevPeriod: string | null,
 *   byCostCode: Record<string, {
 *     desc: string,
 *     periodTotals: Record<string, number>,
 *     total: number,
 *     movement: number,                          // periodTotals[lastPeriod] - periodTotals[prevPeriod]
 *   }>,
 *   bySubCode: Record<string, {
 *     desc: string, periodTotals: Record<string, number>, total: number, movement: number,
 *   }>,                                           // same shape as byCostCode, keyed by raw Sub Code
 *   byResourceCode: Record<string, {
 *     desc: string, periodTotals: Record<string, number>, total: number, movement: number,
 *   }>,                                           // same shape, keyed by immediate parent (Resource) code
 *
 *   groupings: {                                   // ADDITIVE (target contract). Each grouping mode
 *     resourceType: {                               // is split into Direct/Indirect sections — they
 *       Direct: Hierarchy,                           // use fundamentally different business
 *       Indirect: Hierarchy,                         // hierarchies (see below), not just a different
 *     },                                             // classifier.
 *     resourceCode: {
 *       Direct: Hierarchy,
 *       Indirect: Hierarchy,   // SAME OBJECT REFERENCE as resourceType.Indirect — Single
 *     },                       // Result rule: Indirect has no Resource Type/Resource Code
 *   },                         // concept, so its hierarchy is built exactly once and shared.
 *                              // Invariant (true by reference, not just value):
 *                              //   groupings.resourceType.Indirect === groupings.resourceCode.Indirect
 *   // Hierarchy = Record<string, {
 *   //   periodTotals, total, movement,               // all built by the SAME buildHierarchyFromRows()
 *   //   costCodes: Record<string, {                  // (see monthlyActualCostHierarchy.js's
 *   //     desc, periodTotals, total, movement,        // doc-comment for the full architecture
 *   //     subCodes: Record<string, {                  // contract). Every number is a Σ of rows
 *   //       desc, source, periodTotals, total, movement, // already present in `rows` above — nothing
 *   //     }>                                           // is recalculated.
 *   //   }>
 *   // }>
 *   //
 *   // - Direct sections: grouped by Resource Type (resource_dictionary.
 *   //   resource_type, via resolveResourceType) or Resource Code
 *   //   (resource_dict_code, via resolveResourceDictCode) -> Cost Code ->
 *   //   Sub Code, built only from rows whose resolved Cost Code has
 *   //   cost_type='direct'.
 *   // - Indirect sections: grouped by Category (cost_codes.
 *   //   indirect_category_id -> indirect_categories.name, via
 *   //   resolveIndirectCategory — the EXACT SAME categorization Cost
 *   //   Control's buildCumulativeData() uses, not a parallel one) ->
 *   //   Cost Code -> Sub Code. No Resource Code level — Indirect Cost
 *   //   Codes never have one. Built only from rows whose resolved Cost
 *   //   Code has cost_type='indirect'. Uncategorized Indirect Cost Codes
 *   //   group under 'Uncategorized' (same label Cost Control displays).
 *   //   Adding/renaming/reassigning a category on the Cost Codes page is
 *   //   reflected here automatically on next load — zero code changes.
 *   //
 *   // Reconciliation invariant (must always hold, every period and the
 *   // overall total):
 *   //   Σ(groupings.resourceType.Direct) + Σ(groupings.resourceType.Indirect) = grandTotal
 *   //   Σ(groupings.resourceCode.Direct) + Σ(groupings.resourceCode.Indirect) = grandTotal
 *   // Direct/Indirect rows are a non-overlapping partition of the same
 *   // `rows` used everywhere else — the split changes presentation only.
 *
 *   groupingsByPeriod: {                            // ADDITIVE (target contract) — same shape as
 *     resourceType: Record<string, {Direct,Indirect}>, // `groupings`, but each period key maps to a
 *     resourceCode: Record<string, {Direct,Indirect}>, // {Direct, Indirect} pair scoped to that single
 *   },                                               // period only (Single Builder Invariant: built by
 *                                                     // the exact same buildHierarchyFromRows() calls,
 *                                                     // just with period-filtered rows/periods/
 *                                                     // lastPeriod arguments — no alternate aggregation
 *                                                     // path). Same Single Result rule per period:
 *                                                     //   groupingsByPeriod.resourceType[p].Indirect ===
 *                                                     //   groupingsByPeriod.resourceCode[p].Indirect
 *                                                     // Lets Current Month Report (or any single-month
 *                                                     // view) read groupingsByPeriod.resourceType[month]
 *                                                     // directly, with zero additional data loading.
 *   summary: {                                     // ADDITIVE (Phase B) — repackages
 *     grandTotal, grandMovement,                    // grandTotals/grandTotal/grandMovement/
 *     lastPeriod, prevPeriod,                        // lastPeriod/prevPeriod for a sticky
 *     lastPeriodTotal, prevPeriodTotal, periodsCount, // "executive summary" UI section. No new
 *   },                                               // calculation; see buildSummary().
 * }>}
 */
export async function buildMonthlyActualCostData(projectId) {
  if (!projectId) return {
    periods: [], rows: [], grandTotals: {}, grandTotal: 0, cumulativeTotals: {},
    grandMovement: 0, lastPeriod: null, prevPeriod: null, byCostCode: {},
    bySubCode: {}, byResourceCode: {},
    groupings: {
      resourceType: { Direct: {}, Indirect: {} },
      resourceCode: { Direct: {}, Indirect: {} },
    },
    groupingsByPeriod: {
      resourceType: {}, resourceCode: {},
    },
    summary: { grandTotal: 0, grandMovement: 0, lastPeriod: null, prevPeriod: null, lastPeriodTotal: 0, prevPeriodTotal: 0, periodsCount: 0 },
  }

  // Fetch all cost codes for description lookup, AND for resolving a Sub
  // Code (level 2, or a Resource Code at level 4) up to its parent Cost
  // Code (level 1) — needed because IPC/Store transactions are recorded
  // against a Sub Code, not the Cost Code itself, but byCostCode below
  // must be keyed by Cost Code to match buildCumulativeData()'s rows.
  // cost_type and indirect_category_id are fetched here too — the SAME
  // reference columns buildCostControlData.js's buildCumulativeData()
  // reads to distinguish Direct/Indirect and to categorize Indirect Cost
  // Codes. This module reuses that exact categorization rather than
  // inventing a parallel one (see resolveIndirectCategory() in
  // monthlyActualCostHierarchy.js).
  const { data: ccData } = await supabase.from('cost_codes')
    .select('id, code, description, parent_id, level, resource_dict_code, cost_type, indirect_category_id').eq('project_id', projectId)
  const ccDescMap = {}
  ;(ccData || []).forEach(c => { ccDescMap[c.code?.toUpperCase()?.trim()] = c.description })

  // Indirect Categories — user-defined groupings of Indirect Cost Codes,
  // managed entirely on the Cost Codes page (the "🏷 Categories" button)
  // and already used by Cost Control (buildCostControlData.js). Read
  // fresh on every load, exactly like ccData/dictData above — so any
  // category added, renamed, or reassigned on the Cost Codes page is
  // reflected here automatically on next load, with zero code changes.
  const { data: indirectCategoriesData } = await supabase.from('indirect_categories')
    .select('id, name').eq('project_id', projectId)

  // code (lowercased) -> full row, for parent-chain resolution
  const codeToRow = {}
  ;(ccData || []).forEach(c => { if (c.code) codeToRow[c.code.trim().toLowerCase()] = c })
  const idToRow = {}
  ;(ccData || []).forEach(c => { idToRow[c.id] = c })

  // Walk up from a Sub Code / Resource Code to its top-level (level 1) Cost
  // Code. Returns the original code unchanged if it's already level 1, has
  // no parent, or isn't found at all (so callers always get a usable key,
  // never lose the row silently).
  function resolveToCostCode(code) {
    if (!code) return code
    let row = codeToRow[code.trim().toLowerCase()]
    if (!row) return code
    let hops = 0
    while (row.level !== 1 && row.parent_id && hops < 5) {
      const parent = idToRow[row.parent_id]
      if (!parent) break
      row = parent
      hops++
    }
    return row.code
  }

  // Walk up exactly ONE level from a Sub Code to its immediate parent (the
  // Resource Code sitting between it and the top-level Cost Code, in the
  // Cost Code → Resource → Sub Code hierarchy Cost Control's own tree
  // uses). Unlike resolveToCostCode() above (which climbs ALL the way to
  // level 1), this returns the code one step up only — the Resource
  // itself, or the original code unchanged if it has no parent or isn't
  // found (e.g. it's already a top-level Cost Code).
  function resolveToParentCode(code) {
    if (!code) return code
    const row = codeToRow[code.trim().toLowerCase()]
    if (!row || !row.parent_id) return code
    const parent = idToRow[row.parent_id]
    return parent ? parent.code : code
  }

  // ── Resource Type classification (additive, presentation-only) ──
  // resource_dictionary carries the resource_type (material/labour/
  // subcontract/equipment/...) for level-4 "Resource Code" cost_codes
  // rows, linked via cost_codes.resource_dict_code. This is a pure
  // classification lookup, not a financial figure — it never affects any
  // amount, only which branch of the presentation hierarchy a row sits
  // under. The lookup itself (matching dictionary code -> resource_type)
  // and the traversal logic that uses it now live in
  // monthlyActualCostHierarchy.js (buildHierarchyLookups /
  // buildHierarchyFromRows) — this query only fetches the raw reference
  // rows those functions need.
  const { data: dictData } = await supabase.from('resource_dictionary')
    .select('code, resource_type').eq('project_id', projectId)

  // Store transactions are resolved from sub_code instead of the persisted
  // cost_code_id. cost_code_id on store_issues is a derived cache written
  // once at transaction time (see Store.jsx's IssueModal — it always sets
  // cost_code_id from whichever Sub Code is selected, never the reverse)
  // and is NOT re-synchronized after a Sub Code is re-parented to a
  // different Cost Code in cost_codes (CostCodes.jsx's reparentSubCode()
  // only updates cost_codes.parent_id — it does not touch any previously
  // recorded transaction). Actual Cost reporting across this project
  // follows a "Current Hierarchy" policy — buildCumulativeData() rebuilds
  // its Cost Code tree fresh from cost_codes on every load and its own
  // Store RPC (get_actual_cost_by_subcode) joins on sub_code, not
  // cost_code_id — and buildCostControlData.js documents this explicitly:
  // Actual Cost "has no historical snapshot mechanism yet ... reflects
  // current/live totals" (that file's own header comment). This module's
  // own contract (see the file header above) states the same rule:
  // "Callers matching against buildCumulativeData()'s id-keyed rows must
  // join by code, not by id." Resolving Store from sub_code here — then
  // letting realCodeFor()/resolveToCostCode() below climb to the current
  // Level-1 Cost Code — keeps this module consistent with that
  // project-wide policy instead of reflecting a stale cost_code_id frozen
  // at whatever Cost Code the Sub Code happened to sit under when the
  // transaction was recorded. Do not "fix" this back to reading
  // cost_codes(code) through the cost_code_id relation — that
  // reintroduces the exact bug this comment exists to prevent (a Sub Code
  // that was re-parented after the transaction was posted silently keeps
  // reporting under its old, no-longer-correct parent Cost Code).
  //
  // NOTE: payroll_entries and accrual_entries below still resolve their
  // Cost Code via the equivalent cost_codes:sub_code_id(code) embedded
  // relation (a FK, not a text join) — this file's Store fix does not
  // extend to them. The same investigation (does their FK stay
  // synchronized after re-parenting?) has not yet been performed for
  // those two sources and should not be assumed safe by analogy.
  const [ipcRes, payrollRes, storeRes, pettyRes, accrualRes] = await Promise.all([
    supabase.from('ipc_line_items')
      .select('sub_code, this_period_amount, actual_cost_date, ipc_certificates!inner(project_id, status)')
      .eq('ipc_certificates.project_id', projectId)
      .eq('ipc_certificates.status', 'approved'),
    supabase.from('payroll_entries')
      .select('sub_code_id, total_salary, year, month, period_type, cost_codes:sub_code_id(code, description)')
      .eq('project_id', projectId).eq('status', 'approved'),
    supabase.from('store_issues')
      .select('sub_code, amount, issued_date')
      .eq('project_id', projectId),
    supabase.from('petty_cash_vouchers')
      .select('sub_code, budget_code, amount_exc_vat, actual_cost_date')
      .eq('project_id', projectId),
    supabase.from('accrual_entries')
      .select('sub_code_id, amount, actual_cost_date, cost_codes:sub_code_id(code, description)')
      .eq('project_id', projectId),
  ])

  // agg key = "costCode||subCode||source"
  const agg = {}
  const getKey = (cc, sub, src) => `${cc || '—'}||${sub || '—'}||${src}`
  const getDesc = (code) => ccDescMap[(code || '').toUpperCase().trim()] || ''
  const addAmt = (key, ccLabel, subLabel, ccDesc, source, dateStr, amt) => {
    if (!amt || amt <= 0) return
    const period = dateStr ? dateStr.slice(0, 7) : null
    if (!period) return
    if (!agg[key]) agg[key] = { costCode: ccLabel, subCode: subLabel, costCodeDesc: ccDesc, source, periods: {} }
    agg[key].periods[period] = (agg[key].periods[period] || 0) + amt
  }

  // IPC — grouped by sub_code
  ;(ipcRes.data || []).forEach(l => {
    const sub = l.sub_code || '—'
    addAmt(getKey('IPC', sub, 'IPC'), 'IPC', sub, getDesc(sub), 'IPC', l.actual_cost_date, parseFloat(l.this_period_amount) || 0)
  })

  // Payroll — grouped by cost_code (sub code)
  ;(payrollRes.data || []).forEach(e => {
    const cc = e.cost_codes?.code || e.sub_code_id || '—'
    const desc = e.cost_codes?.description || ''
    const mo = parseInt(e.month) || 1
    const yr = parseInt(e.year) || new Date().getFullYear()
    const period = e.period_type === 'yearly' ? `${yr}-01` : `${yr}-${String(mo).padStart(2, '0')}`
    addAmt(getKey(cc, cc, 'Payroll'), cc, cc, getDesc(cc) || desc, 'Payroll', period + '-01', parseFloat(e.total_salary) || 0)
  })

  // Store — cc is resolved from sub_code (via getDesc/ccDescMap, and
  // subsequently via realCodeFor()/resolveToCostCode() below), NOT from
  // the row's own persisted cost_code_id. See the long comment above the
  // Promise.all() above for why. getDesc(sub) alone (no fallback to a
  // cost_codes(description) embedded-relation value) is intentional:
  // ccDescMap is built from the full, current cost_codes table (all
  // levels), so any sub_code that matches a real code always resolves a
  // description from it — a fallback to the old JOIN's description would
  // silently reintroduce the same stale-relation risk this fix removes.
  ;(storeRes.data || []).forEach(i => {
    const sub = i.sub_code || '—'
    const cc = sub
    const desc = getDesc(sub)
    addAmt(getKey(cc, sub, 'Store'), cc, sub, desc, 'Store', i.issued_date, parseFloat(i.amount) || 0)
  })

  // Petty Cash
  ;(pettyRes.data || []).forEach(v => {
    const sub = v.sub_code || v.budget_code || '—'
    addAmt(getKey('Petty Cash', sub, 'Petty Cash'), 'Petty Cash', sub, getDesc(sub), 'Petty Cash', v.actual_cost_date, parseFloat(v.amount_exc_vat) || 0)
  })

  // Accrual
  ;(accrualRes.data || []).forEach(a => {
    const cc = a.cost_codes?.code || a.sub_code_id || '—'
    const desc = a.cost_codes?.description || ''
    addAmt(getKey(cc, cc, 'Accrual'), cc, cc, getDesc(cc) || desc, 'Accrual', a.actual_cost_date, parseFloat(a.amount) || 0)
  })

  const periods = [...new Set(Object.values(agg).flatMap(r => Object.keys(r.periods)))]
    .sort((a, b) => periodToNum(a) - periodToNum(b))

  // Sort rows: by source order then by code (same order as MonthlyActualCost.jsx's display)
  const SOURCE_ORDER = ['IPC', 'Payroll', 'Store', 'Petty Cash', 'Accrual']
  const rows = Object.values(agg).sort((a, b) => {
    const sa = SOURCE_ORDER.indexOf(a.source)
    const sb = SOURCE_ORDER.indexOf(b.source)
    if (sa !== sb) return sa - sb
    return a.costCode.localeCompare(b.costCode)
  })

  // Grand totals per period (unfiltered — callers wanting a period filter,
  // like MonthlyActualCost.jsx's selectedPeriods checkbox filter, should
  // filter `periods`/`rows` themselves before deriving totals, same as
  // that screen already does for its own display-only filtering)
  const grandTotals = {}
  periods.forEach(p => {
    grandTotals[p] = rows.reduce((s, r) => s + (r.periods[p] || 0), 0)
  })
  const grandTotal = Object.values(grandTotals).reduce((s, v) => s + v, 0)

  const cumulativeTotals = {}
  let running = 0
  periods.forEach(p => { running += grandTotals[p] || 0; cumulativeTotals[p] = running })

  // Movement = last two periods PRESENT IN THE DATA, not calendar this-month/last-month
  const lastPeriod = periods[periods.length - 1] || null
  const prevPeriod = periods[periods.length - 2] || null
  const grandMovement = lastPeriod ? (grandTotals[lastPeriod] || 0) - (prevPeriod ? grandTotals[prevPeriod] || 0 : 0) : 0

  // Per-Cost-Code breakdown — for Executive Report's "This Period" (Movement)
  // column, keyed by the ACTUAL top-level Cost Code (level 1), not by
  // source label or raw Sub Code. Two of the five sources (IPC, Petty Cash)
  // store 'IPC'/'Petty Cash' as a placeholder in costCode — grouping the
  // on-screen table by data source rather than by Cost Code — while their
  // real code lives in subCode instead, and that subCode is itself usually
  // a level-2/level-4 code that must be walked up to its level-1 parent to
  // match buildCumulativeData()'s Cost Code rows. The other three sources
  // (Payroll, Store, Accrual) already use a real code in costCode, but that
  // code can also be a Sub Code rather than the top-level Cost Code, so it
  // goes through the same resolution step.
  const realCodeFor = (r) => resolveToCostCode((r.source === 'IPC' || r.source === 'Petty Cash') ? r.subCode : r.costCode)

  // resolvedCostCodeDesc — ADDITIVE field, added to every row alongside the
  // existing costCodeDesc (which is left completely unchanged for backward
  // compatibility: it continues to mean "the description of the code the
  // transaction was originally posted against", i.e. correct for a Sub
  // Code / leaf-level display, per its original semantics).
  // resolvedCostCodeDesc instead answers a different question: "what is
  // the description of this row's resolved top-level (Level-1) Cost
  // Code?" — computed via the SAME realCodeFor() resolution already used
  // for byCostCode and the hierarchy (monthlyActualCostHierarchy.js), so
  // there is exactly one resolution path for "which Level-1 Cost Code
  // does this row roll up to" in the whole system. Consumers that group
  // rows under a parent Cost Code node (e.g. MonthlyActualCost.jsx's "By
  // Source" table) should read resolvedCostCodeDesc for that Cost Code
  // node's own description, and continue reading costCodeDesc only for
  // the leaf Sub Code row.
  rows.forEach(r => { r.resolvedCostCodeDesc = getDesc(realCodeFor(r)) })

  const byCostCode = {}
  const ccGroups = {}
  for (const r of rows) {
    const key = realCodeFor(r)
    if (!key || key === '—') continue
    if (!ccGroups[key]) ccGroups[key] = { desc: r.costCodeDesc, subs: [] }
    ccGroups[key].subs.push(r)
  }
  for (const [cc, { desc, subs }] of Object.entries(ccGroups)) {
    const periodTotals = {}
    periods.forEach(p => { periodTotals[p] = subs.reduce((s, r) => s + (r.periods[p] || 0), 0) })
    const total = Object.values(periodTotals).reduce((s, v) => s + v, 0)
    const movement = lastPeriod ? (periodTotals[lastPeriod] || 0) - (prevPeriod ? periodTotals[prevPeriod] || 0 : 0) : 0
    byCostCode[cc] = { desc, periodTotals, total, movement }
  }

  // bySubCode — an ADDITIONAL breakdown, at the finer Resource/Sub Code
  // grain each row is already individually tracked at (agg's own key
  // includes the real subCode/resource code before any resolveToCostCode()
  // roll-up). This does not change byCostCode, rows, or any existing field
  // above — it's purely an extra index for consumers (Cost Control PDF)
  // that need Movement matched at the same Resource/Sub Code granularity
  // the Cost Control tree itself displays, not just the top-level Cost
  // Code. Same exact Movement formula as byCostCode
  // (periodTotals[lastPeriod] - periodTotals[prevPeriod]), applied one
  // level finer. Two or more raw rows can share the same real code across
  // different sources (e.g. a Sub Code appearing in both Store and Petty
  // Cash) — these are summed together here, exactly as byCostCode already
  // sums multiple rows under one Cost Code.
  // Every row already carries its real Sub Code/Resource code in
  // r.subCode directly (set by addAmt() at ingestion time for all five
  // sources) — no resolution/roll-up needed here, unlike realCodeFor()
  // above which walks up to the parent Cost Code.
  const bySubCode = {}
  const subGroups = {}
  for (const r of rows) {
    const key = r.subCode
    if (!key || key === '—') continue
    if (!subGroups[key]) subGroups[key] = { desc: r.costCodeDesc, subs: [] }
    subGroups[key].subs.push(r)
  }
  for (const [sub, { desc, subs }] of Object.entries(subGroups)) {
    const periodTotals = {}
    periods.forEach(p => { periodTotals[p] = subs.reduce((s, r) => s + (r.periods[p] || 0), 0) })
    const total = Object.values(periodTotals).reduce((s, v) => s + v, 0)
    const movement = lastPeriod ? (periodTotals[lastPeriod] || 0) - (prevPeriod ? periodTotals[prevPeriod] || 0 : 0) : 0
    bySubCode[sub] = { desc, periodTotals, total, movement }
  }

  // byResourceCode — a THIRD additional breakdown, one level coarser than
  // bySubCode: keyed by each Sub Code's immediate parent (the Resource
  // Code sitting between it and the top-level Cost Code, in Cost
  // Control's own Cost Code → Resource → Sub Code hierarchy), via
  // resolveToParentCode() above (climbs exactly one level, unlike
  // resolveToCostCode()'s full climb to level 1). This exists because
  // Resources themselves never carry transactions directly — every IPC/
  // Store/Payroll/Petty Cash/Accrual row is recorded against a Sub Code —
  // so bySubCode alone has no entry for a Resource's own code. Consumers
  // (Cost Control PDF, on-screen table) that display Movement at Resource
  // granularity read this directly instead of summing bySubCode entries
  // themselves — keeping this aggregation inside the build layer, not
  // duplicated in a presentation layer. Same exact Movement formula as
  // byCostCode/bySubCode; does not change any field above.
  const byResourceCode = {}
  const resourceGroups = {}
  for (const r of rows) {
    if (!r.subCode || r.subCode === '—') continue
    const key = resolveToParentCode(r.subCode)
    if (!key || key === '—') continue
    if (!resourceGroups[key]) resourceGroups[key] = { desc: r.costCodeDesc, subs: [] }
    resourceGroups[key].subs.push(r)
  }
  for (const [res, { desc, subs }] of Object.entries(resourceGroups)) {
    const periodTotals = {}
    periods.forEach(p => { periodTotals[p] = subs.reduce((s, r) => s + (r.periods[p] || 0), 0) })
    const total = Object.values(periodTotals).reduce((s, v) => s + v, 0)
    const movement = lastPeriod ? (periodTotals[lastPeriod] || 0) - (prevPeriod ? periodTotals[prevPeriod] || 0 : 0) : 0
    byResourceCode[res] = { desc, periodTotals, total, movement }
  }

  // ── Additive presentation outputs (Phase B + Phase D + Groupings migration) ──
  // All hierarchy projections below are pure projections of `rows`, built
  // by the SAME buildHierarchyFromRows() function imported above, using
  // the SAME lookups — see monthlyActualCostHierarchy.js's module
  // doc-comment for the full architecture contract, including the Single
  // Builder Invariant this satisfies:
  //   for every period P:
  //     groupings.resourceType (period-scoped) === buildHierarchyFromRows(
  //     rows filtered for P, [P], P, null, lookups, resolveResourceType)
  //   for the full dataset:
  //     groupings.resourceType === buildHierarchyFromRows(rows, periods,
  //     lastPeriod, prevPeriod, lookups, resolveResourceType)
  //   and identically for groupings.resourceCode, using
  //   resolveResourceDictCode as the classifier instead — same builder,
  //   same lookups, only the first-level grouping key differs.
  // Lookups are built once here and reused for every projection below —
  // one data load, one source of truth, multiple projections.
  const lookups = buildHierarchyLookups(ccData, dictData, indirectCategoriesData)

  // groupings — the target contract. Each grouping mode is now split into
  // Direct and Indirect sections, because they use fundamentally
  // different business hierarchies (not just a different classifier):
  //   - Direct: Resource Type / Resource Code -> Cost Code -> Sub Code,
  //     classified via resource_dictionary (resolveResourceType /
  //     resolveResourceDictCode).
  //   - Indirect: Category -> Cost Code -> Sub Code (no Resource Code
  //     level at all — Indirect Cost Codes never have one, exactly as
  //     buildCostControlData.js documents: "Indirect budget comes from
  //     tender_indirect_items (Cost Code level, single-level — no
  //     Resource Code for Indirect)"), classified via
  //     cost_codes.indirect_category_id (resolveIndirectCategory) — the
  //     EXACT SAME categorization Cost Control uses, not a parallel one.
  //     Adding, renaming, or reassigning a category on the Cost Codes
  //     page is reflected here automatically on next load.
  //
  // Single Result rule: the Indirect hierarchy is built ONCE (it has no
  // concept of "Resource Type" vs "Resource Code" — Indirect's hierarchy
  // is identical either way) and the SAME object reference is used under
  // both groupings.resourceType.Indirect and groupings.resourceCode.
  // Indirect. This is checked by the invariant below, true BY REFERENCE:
  //   groupings.resourceType.Indirect === groupings.resourceCode.Indirect
  //   (and identically for each groupingsByPeriod[period] projection)
  // If Cost Control's categorization changes, there is exactly one
  // Indirect hierarchy to rebuild here, never two identical copies.
  //
  // Reconciliation invariant (must always hold, for every period and the
  // overall total):
  //   Σ(groupings.resourceType.Direct) + Σ(groupings.resourceType.Indirect) = grandTotal
  //   Σ(groupings.resourceCode.Direct) + Σ(groupings.resourceCode.Indirect) = grandTotal
  // The split only changes the presentation hierarchy — it can never
  // change any financial value, since Direct/Indirect rows are a
  // non-overlapping partition of the exact same `rows` used everywhere
  // else in this file.
  const directRows = rows.filter(r => !isIndirectCostCode(realCodeFor(r), lookups))
  const indirectRows = rows.filter(r => isIndirectCostCode(realCodeFor(r), lookups))

  const indirectHierarchy = buildHierarchyFromRows(indirectRows, periods, lastPeriod, prevPeriod, lookups, resolveIndirectCategory)

  const groupings = {
    resourceType: {
      Direct: buildHierarchyFromRows(directRows, periods, lastPeriod, prevPeriod, lookups, resolveResourceType),
      Indirect: indirectHierarchy,
    },
    resourceCode: {
      Direct: buildHierarchyFromRows(directRows, periods, lastPeriod, prevPeriod, lookups, resolveResourceDictCode),
      Indirect: indirectHierarchy, // same reference as above — Single Result rule
    },
  }

  // Per-period projections for every grouping mode — built eagerly here,
  // during the same load, so report components (e.g. Current Month
  // Report) never need a second data-loading path for ANY grouping mode,
  // present or future. Same Direct/Indirect split and Single Result rule
  // as `groupings` above, applied per period.
  const groupingsByPeriod = { resourceType: {}, resourceCode: {} }
  periods.forEach(p => {
    const directRowsForP = directRows
      .filter(r => (r.periods[p] || 0) !== 0)
      .map(r => ({ ...r, periods: { [p]: r.periods[p] } }))
    const indirectRowsForP = indirectRows
      .filter(r => (r.periods[p] || 0) !== 0)
      .map(r => ({ ...r, periods: { [p]: r.periods[p] } }))

    const indirectHierarchyForP = buildHierarchyFromRows(indirectRowsForP, [p], p, null, lookups, resolveIndirectCategory)

    groupingsByPeriod.resourceType[p] = {
      Direct: buildHierarchyFromRows(directRowsForP, [p], p, null, lookups, resolveResourceType),
      Indirect: indirectHierarchyForP,
    }
    groupingsByPeriod.resourceCode[p] = {
      Direct: buildHierarchyFromRows(directRowsForP, [p], p, null, lookups, resolveResourceDictCode),
      Indirect: indirectHierarchyForP, // same reference as above — Single Result rule
    }
  })

  const summary = buildSummary(grandTotals, grandTotal, grandMovement, lastPeriod, prevPeriod, periods)

  return {
    periods, rows, grandTotals, grandTotal, cumulativeTotals, grandMovement,
    lastPeriod, prevPeriod, byCostCode, bySubCode, byResourceCode,
    groupings, groupingsByPeriod,
    summary,
  }
}
